/**
 * Internationalization (i18n) service
 * Provides translation functionality for the application
 */
import { languageStore, availableLanguages, type AvailableLanguage } from './i18n-store';
import { loadTranslations } from './translation-loader';

// Re-export for convenience
export { availableLanguages, type AvailableLanguage };

export const STORAGE_KEY = 'perfect-ai-assistant.lang';

// Cache for loaded translations
const loadedTranslations = new Map<AvailableLanguage, Record<string, string>>();
const loadingPromises = new Map<
    AvailableLanguage,
    Promise<Record<string, string>>
>();

/**
 * Get the current language from store
 * @returns The current language code, defaults to 'en'
 */
export function getCurrentLanguage(): AvailableLanguage {
    // Always use the store value to ensure consistency between server and client
    // The store is initialized by the LanguageProvider with the value from the cookie
    return languageStore.getLanguage();
}

/**
 * Synchronously get translations for a language if already loaded
 * @param language The language to get translations for
 * @returns Translation object or null if not loaded
 */
function getLoadedTranslations(
    language: AvailableLanguage,
): Record<string, string> | null {
    return loadedTranslations.get(language) || null;
}

/**
 * Initialize translations directly from server-loaded data
 * @param language The language to initialize
 * @param translations The translation map to use
 */
export function initializeTranslations(
    language: AvailableLanguage,
    translations: Record<string, string>,
): void {
    loadedTranslations.set(language, translations);
}

/**
 * Preload translations for a language
 * @param language The language to preload
 */
export async function preloadTranslations(
    language: AvailableLanguage,
): Promise<void> {
    if (loadedTranslations.has(language)) {
        return; // Already loaded
    }

    if (loadingPromises.has(language)) {
        await loadingPromises.get(language); // Wait for existing loading
        return;
    }

    const loadingPromise = loadTranslations(language);
    loadingPromises.set(language, loadingPromise);

    try {
        const translations = await loadingPromise;
        loadedTranslations.set(language, translations);
    } catch (error) {
        loadedTranslations.set(language, {}); // Set empty object on error
    } finally {
        loadingPromises.delete(language);
    }
}

/**
 * Translate function that returns a localized string
 * Works both on server (using store) and client (using localStorage)
 * @param key - The translation key to look up
 * @param value - The default value to return if translation not found
 * @returns The translated string or fallback value
 */
export function t(key: string, value: string): string {
    const currentLang = getCurrentLanguage();

    // For English, return the original value
    if (currentLang === 'en') {
        return value;
    }

    // Try to get translations synchronously if already loaded
    const translations = getLoadedTranslations(currentLang);

    if (translations && translations[key]) {
        return translations[key];
    }

    // If translations aren't loaded, trigger async loading (fire and forget)
    // but return fallback value for now
    if (
        !loadedTranslations.has(currentLang) &&
        !loadingPromises.has(currentLang)
    ) {
        preloadTranslations(currentLang).catch(() => {
            // Silently handle errors
        });
    }

    // Return fallback
    return value;
}
